import React, { useState } from 'react';
import styled from 'styled-components';
import { PropertyImage } from '../../ducks/home';
import { ReactComponent as ArrowLeft } from "../../assets/arrow-left.svg"
import { ReactComponent as ArrowRight } from "../../assets/arrow-right.svg"
import { buildImageUrl } from '../../utils/imageUrl';
import { SimpleWatermark } from '../atoms/SimpleWatermark';
import { PreparedWatermarkImage, prepareWatermarkedImage } from '../../utils/watermarkPreloader';

interface ImageSlideshowProps {
  images: PropertyImage[];
  aspectRatio?: string; // desktop/tablet aspect ratio
  mobileAspectRatio?: string; // mobile aspect ratio
}

export const ImageSlideshow: React.FC<ImageSlideshowProps> = ({ images, aspectRatio = '16/9', mobileAspectRatio = '4/3' }) => {
  const [current, setCurrent] = useState(0);
  const [preparedSlides, setPreparedSlides] = useState<Record<number, PreparedWatermarkImage>>({});
  const total = images.length;

  const goTo = (idx: number) => setCurrent((idx + total) % total);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  // Use refs for touch coordinates to avoid re-renders during gestures
  const touchStart = React.useRef<number | null>(null);
  const touchEnd = React.useRef<number | null>(null);

  // Minimum swipe distance (in px) 
  const minSwipeDistance = 50;

  React.useEffect(() => {
    setCurrent(0);
    setPreparedSlides({});
  }, [images]);

  React.useEffect(() => {
    if (total === 0) {
      return;
    }

    let isCancelled = false;

    const warmSlide = async (index: number) => {
      const image = images[index];
      if (!image) {
        return;
      }

      const prepared = await prepareWatermarkedImage({
        src: buildImageUrl(image.image),
        watermarkOpacity: 0.5,
        watermarkPosition: 'center',
        watermarkSize: 'large',
      });

      if (isCancelled) {
        return;
      }

      setPreparedSlides((previous) => {
        const currentSlide = previous[index];
        if (
          currentSlide &&
          currentSlide.sourceSrc === prepared.sourceSrc &&
          currentSlide.displaySrc === prepared.displaySrc &&
          currentSlide.needsOverlay === prepared.needsOverlay
        ) {
          return previous;
        }

        return {
          ...previous,
          [index]: prepared,
        };
      });
    };

    const orderedIndices = Array.from({ length: total }, (_, offset) => (current + offset) % total);
    const priorityIndices = orderedIndices.slice(0, Math.min(2, total));
    const backgroundIndices = orderedIndices.slice(priorityIndices.length);

    Promise.all(priorityIndices.map((index) => warmSlide(index))).then(() => {
      backgroundIndices.forEach((index) => {
        void warmSlide(index);
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [images, current, total]);

  // Touch event handlers (mobile swipe only - desktop uses arrows/dots)
  const onTouchStart = (e: React.TouchEvent) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStart.current === null || touchEnd.current === null) return;

    const distance = touchStart.current - touchEnd.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      next();
    }
    if (isRightSwipe) {
      prev();
    }

    // Reset refs
    touchStart.current = null;
    touchEnd.current = null;
  };

  // Get the image URL
  const currentImage = images[current];
  const imageUrl = buildImageUrl(currentImage.image);
  const preparedCurrentSlide = preparedSlides[current];

  return (
    <Container>
      <SlideshowContainer
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <Arrow $left onClick={prev} aria-label="Previous image">
          <ArrowLeft />
        </Arrow>
        <SlideshowWrapper $aspectRatio={aspectRatio} $mobileAspectRatio={mobileAspectRatio}>
          <SimpleWatermark
            src={imageUrl}
            alt={`Slide ${current + 1}`}
            watermarkOpacity={0.5}
            watermarkPosition="center"
            watermarkSize="large"
            preparedImage={preparedCurrentSlide}
          />
        </SlideshowWrapper>
        <Arrow onClick={next} aria-label="Next image">
          <ArrowRight />
        </Arrow>
      </SlideshowContainer>
      <Dots>
        {images.map((_, idx) => (
          <Dot
            key={idx}
            $active={idx === current}
            onClick={() => goTo(idx)}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </Dots>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  width: 100%;
  
  @media (max-width: 768px) {
    margin-top: 16px;
  }
  
  @media (max-width: 480px) {
    margin-top: 12px;
  }
`;

const Arrow = styled.button<{ $left?: boolean }>`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  color: #666;
  font-size: 48px;
  cursor: pointer;
  z-index: 20;
  opacity: 0.9;
  transition: all 0.2s ease;
  border-radius: 50%;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  min-width: 56px;
  min-height: 56px;
  top: 50%;
  transform: translateY(-50%);
  ${({ $left }) => ($left ? 'left: 12px;' : 'right: 12px;')}
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  /* Ensure SVG icons are properly sized */
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    min-height: 36px;
    font-size: 32px;
    opacity: 0.8;
    background: rgba(255, 255, 255, 0.9);
    
    svg {
      width: 14px;
      height: 14px;
    }
    
    &:hover {
      opacity: 0.95;
    }
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    font-size: 28px;
    opacity: 0.75;
    background: rgba(255, 255, 255, 0.85);
    
    svg {
      width: 12px;
      height: 12px;
    }
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const SlideshowContainer = styled.div`
  display: block;
  position: relative;
  width: 100%;
  padding: 0;
  /* Enable horizontal swipe while allowing vertical scroll */
  touch-action: pan-y pinch-zoom;
  user-select: none;
  -webkit-user-select: none;
  
  @media (max-width: 768px) {
    padding: 0;
  }
  
  @media (max-width: 480px) {
    padding: 0;
  }
`;



const SlideshowWrapper = styled.div<{ $aspectRatio: string; $mobileAspectRatio: string }>`
  width: 100%;
  max-width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  background: #f8fafc;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  /* Prevent image drag which can interfere with swipe */
  -webkit-user-drag: none;
  
  & img {
    -webkit-user-drag: none;
    user-select: none;
    -webkit-user-select: none;
  }
  
  @media (max-width: 768px) {
    /* Make the image taller on mobile */
    aspect-ratio: ${({ $mobileAspectRatio }) => $mobileAspectRatio};
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    border-radius: 4px;
  }
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin: 24px 0 0 0;
  flex-wrap: wrap;
  padding: 0 8px;
  
  @media (max-width: 768px) {
    gap: 14px;
    margin: 20px 0 0 0;
    padding: 0 6px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    margin: 16px 0 0 0;
    padding: 0 4px;
  }
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${({ $active }) => ($active ? '#1a2a3a' : '#d3d7db')};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  min-width: 12px;
  min-height: 12px;
  padding: 0;
  margin: 0;
  
  /* Ensure perfect circle */
  aspect-ratio: 1;
  
  /* Better touch target for mobile */
  @media (max-width: 768px) {
    /* Increase touch target while keeping visual size */
    padding: 4px;
    margin: -4px;
  }
  
  &:hover {
    background: ${({ $active }) => ($active ? '#1a2a3a' : '#b0b8c0')};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
    min-width: 10px;
    min-height: 10px;
  }
  
  @media (max-width: 480px) {
    width: 8px;
    height: 8px;
    min-width: 8px;
    min-height: 8px;
  }
`;
